import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Privacy Policy - Sales Intercept</title>
        <meta name="description" content="Review Sales Intercept's Privacy Policy to understand how we collect, use, and protect your personal information across our services. Your data privacy is important to us." />
      </Helmet>
    <div className="min-h-screen bg-gray-50 pt-40 pb-12">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-md p-8">
          <h1 className="text-3xl font-bold mb-8">Privacy Policy</h1>
          <p className="text-gray-600 mb-6">Effective Date: April 22, 2025</p>

          <div className="prose prose-gray max-w-none">
            <p className="mb-6">
              This Privacy Policy explains how Developer Squad, Inc., doing business as Sales Intercept
              ("Sales Intercept," "Company," "we," "our," or "us"), collects, uses, discloses, and
              safeguards information when you visit or use any of our websites (including
              salesintercept.com and developersquad.com), web or mobile applications, browser
              extensions, SaaS platforms, and related online services we own or control (collectively, the
              "Services").
            </p>

            <p className="mb-6">
              By accessing or using the Services, you acknowledge that you have read, understood, and
              agree to our collection, use, and disclosure practices described below and in our Terms of
              Service. If you do not agree, please do not use the Services.
            </p>

            <h2 className="text-2xl font-bold mt-8 mb-4">1. Scope</h2>
            <p className="mb-6">
              This Privacy Policy applies only to information collected online through the Services. It
              does not apply to information collected offline, through third-party websites or services
              that we do not control, or by any subsidiary or affiliate operating under a separate privacy
              notice.
            </p>

            <h2 className="text-2xl font-bold mt-8 mb-4">2. Information We Collect</h2>
            <p className="mb-4">We collect the following categories of information:</p>
            <table className="w-full mb-6">
              <thead>
                <tr>
                  <th className="text-left py-2">Category</th>
                  <th className="text-left py-2">Examples</th>
                  <th className="text-left py-2">Source</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="py-2">Identifiers</td>
                  <td className="py-2">Name, postal address, email, phone, username, unique device identifiers, IP address</td>
                  <td className="py-2">Directly from you; automatically from your device</td>
                </tr>
                <tr>
                  <td className="py-2">Commercial Information</td>
                  <td className="py-2">Purchase history, subscription tier, payment method (tokenized), order notes</td>
                  <td className="py-2">Directly from you; our payment processors</td>
                </tr>
                <tr>
                  <td className="py-2">Internet/Network Activity</td>
                  <td className="py-2">Log files, pages viewed, referring URLs, clicks, timestamps</td>
                  <td className="py-2">Automatically from your device</td>
                </tr>
                <tr>
                  <td className="py-2">Geolocation Data</td>
                  <td className="py-2">Approximate location derived from IP address</td>
                  <td className="py-2">Automatically from your device</td>
                </tr>
                <tr>
                  <td className="py-2">User-Generated Content</td>
                  <td className="py-2">Posts, chat messages, support tickets, survey responses</td>
                  <td className="py-2">Directly from you</td>
                </tr>
                <tr>
                  <td className="py-2">Professional Information</td>
                  <td className="py-2">Company name, job title, industry</td>
                  <td className="py-2">Directly from you</td>
                </tr>
              </tbody>
            </table>
            <p className="mb-6">
              We do not knowingly collect sensitive personal information (e.g., social security numbers,
              precise geolocation, or health data).
            </p>

            <h2 className="text-2xl font-bold mt-8 mb-4">3. Cookies & Similar Technologies</h2>
            <p className="mb-4">We and our analytics/advertising partners use:</p>
            <ul className="list-disc pl-6 mb-6">
              <li>Cookies & Local Storage: Small files placed on your device.</li>
              <li>Pixel Tags & Web Beacons: Code snippets that record usage.</li>
              <li>SDKs (for mobile): Code packages that enable analytics or ads.</li>
            </ul>
            <p className="mb-6">
              You can manage cookies via your browser settings and, where required by law, through our
              cookie consent banner.
            </p>

            <h2 className="text-2xl font-bold mt-8 mb-4">4. How We Use Your Information</h2>
            <p className="mb-4">We process your information to:</p>
            <ul className="list-disc pl-6 mb-6">
              <li>Provide and maintain the Services (e.g., create accounts, process orders, deliver content).</li>
              <li>Improve and personalize the Services (e.g., remember preferences, A/B testing, analytics).</li>
              <li>Market and advertise (e.g., send newsletters, show relevant ads, run promotions—subject to opt-out choices).</li>
              <li>Secure and prevent fraud (e.g., detect abuse, enforce Terms, investigate suspicious activity).</li>
              <li>Comply with legal obligations (e.g., tax, accounting, lawful requests).</li>
            </ul>

            <h2 className="text-2xl font-bold mt-8 mb-4">5. Legal Bases for Processing (EEA/UK Users)</h2>
            <p className="mb-4">For users in the European Economic Area (EEA) or United Kingdom (UK), we rely on:</p>
            <ul className="list-disc pl-6 mb-6">
              <li>Contractual necessity: To provide the Services you request.</li>
              <li>Legitimate interests: To secure, improve, and market our Services (balanced against your rights).</li>
              <li>Consent: For non-essential cookies or email marketing where required.</li>
              <li>Legal obligation: To meet regulatory or law-enforcement requirements.</li>
            </ul>

            <h2 className="text-2xl font-bold mt-8 mb-4">6. How We Share Information</h2>
            <p className="mb-4">We may share your information with:</p>
            <table className="w-full mb-6">
              <thead>
                <tr>
                  <th className="text-left py-2">Recipient</th>
                  <th className="text-left py-2">Purpose</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="py-2">Service Providers (e.g., hosting, payment processing, analytics, customer support platforms)</td>
                  <td className="py-2">Operate the Services under confidentiality agreements</td>
                </tr>
                <tr>
                  <td className="py-2">Business Partners & Resellers</td>
                  <td className="py-2">Joint marketing, integration support (only if you engage with them)</td>
                </tr>
                <tr>
                  <td className="py-2">Advertising Partners</td>
                  <td className="py-2">Serve or measure ads (never your payment details)</td>
                </tr>
                <tr>
                  <td className="py-2">Legal & Safety</td>
                  <td className="py-2">Respond to court orders, protect rights, prevent fraud or harm</td>
                </tr>
                <tr>
                  <td className="py-2">Successors</td>
                  <td className="py-2">In connection with a merger, acquisition, or asset sale (with notice and choices as required by law)</td>
                </tr>
              </tbody>
            </table>
            <p className="mb-6">
              Certain third-party services integrated into or resold through our Services may have their
              own privacy policies, and we are not responsible for their data practices. We do not sell
              personal information for monetary consideration; however, certain sharing for cross-context behavioral advertising may be considered a "sale" or "share" under U.S. state
              privacy laws. See Section 9 for your opt-out rights.
            </p>

            <h2 className="text-2xl font-bold mt-8 mb-4">7. International Transfers</h2>
            <p className="mb-6">
              We are headquartered in the United States. If you access the Services from outside the
              U.S., your information may be transferred to, stored, and processed in the U.S. or other
              countries that may not provide the same level of data protection. We use approved legal
              mechanisms (e.g., Standard Contractual Clauses) for cross-border transfers when
              required.
            </p>

            <h2 className="text-2xl font-bold mt-8 mb-4">8. Security</h2>
            <p className="mb-6">
              We employ administrative, technical, and physical safeguards designed to protect
              information against unauthorized access, destruction, or alteration. No method of
              transmission or storage is 100% secure; we cannot guarantee absolute security.
            </p>

            <h2 className="text-2xl font-bold mt-8 mb-4">9. Your Privacy Choices & Rights</h2>
            <p className="mb-4">Your rights depend on your jurisdiction:</p>
            <table className="w-full mb-6">
              <thead>
                <tr>
                  <th className="text-left py-2">Jurisdiction Law</th>
                  <th className="text-left py-2">Key Rights & How to Exercise</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="py-2">EEA/UK GDPR</td>
                  <td className="py-2">Access, rectification, erasure, restriction, data portability, object, withdraw consent. Email legal@salesintercept.com.</td>
                </tr>
                <tr>
                  <td className="py-2">California CCPA/CPRA</td>
                  <td className="py-2">Know, delete, correct, opt-out of "sale"/"sharing," limit sensitive info, non-discrimination. Visit salesintercept.com/privacyccpa or call +1-888-935-4479.</td>
                </tr>
                <tr>
                  <td className="py-2">Marketing Emails</td>
                  <td className="py-2">Click "unsubscribe" in any email or update preferences in your account dashboard.</td>
                </tr>
                <tr>
                  <td className="py-2">Cookies/Ads</td>
                  <td className="py-2">Use our cookie banner or browser settings; visit optout.aboutads.info for interest-based advertising choices.</td>
                </tr>
              </tbody>
            </table>
            <p className="mb-6">
              We will verify your request and respond within the timeframe required by law.
            </p>

            <h2 className="text-2xl font-bold mt-8 mb-4">10. Data Retention</h2>
            <p className="mb-6">
              We retain information for as long as necessary to fulfill the purposes outlined in this Policy,
              comply with legal obligations, resolve disputes, enforce agreements, or as otherwise
              permitted by law. If a third-party service is discontinued, we will securely delete or
              anonymize associated data unless required for legal or operational purposes. When data is
              no longer needed, we securely delete or anonymize it.
            </p>

            <h2 className="text-2xl font-bold mt-8 mb-4">11. Third-Party Links & Services</h2>
            <p className="mb-6">
              Our Services may link to or integrate with third-party sites, plug-ins, or applications. We do
              not control those third parties and are not responsible for their privacy practices. We
              encourage you to review their privacy notices.
            </p>

            <h2 className="text-2xl font-bold mt-8 mb-4">12. Children's Privacy</h2>
            <p className="mb-6">
              The Services are not directed to, and we do not knowingly collect personal information
              from, children under 13 (16 in the EEA). If we learn we have inadvertently collected such
              data, we will delete it promptly. Parents who believe we have collected information from a
              child may contact us at legal@salesintercept.com.
            </p>

            <h2 className="text-2xl font-bold mt-8 mb-4">13. Changes to This Policy</h2>
            <p className="mb-6">
              We may update this Privacy Policy periodically. If changes are material, we will post the
              revised Policy and, where required, notify you via email or website announcement. The
              "Effective Date" at the top indicates when the Policy was last revised.
            </p>

            <h2 className="text-2xl font-bold mt-8 mb-4">14. Contact Us</h2>
            <p className="mb-6">For questions about this Privacy Policy, contact us at:</p>
            <address className="mb-6 not-italic">
              Sales Intercept / Developer Squad, Inc.<br />
              7954 Transit Road #232<br />
              Williamsville, NY 14221 USA<br />
              Email: legal@salesintercept.com
            </address>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default PrivacyPolicy;